import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "preview", "writeTab", "previewTab", "fileInput", "uploadStatus"]
  static values = { uploadUrl: String }

  connect() {
    this.mentionDropdown = null
    this.mentionIndex = -1
    this.mentionResults = []
    this.debounceTimer = null

    this.editorTarget.addEventListener("input", this.handleEditorInput.bind(this))
    this.editorTarget.addEventListener("keydown", this.handleMentionKeydown.bind(this))
    document.addEventListener("click", this.closeMentionDropdown.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.closeMentionDropdown.bind(this))
    if (this.mentionDropdown) {
      this.mentionDropdown.remove()
    }
  }

  write(event) {
    event.preventDefault()
    this.editorTarget.classList.remove("d-none")
    this.previewTarget.classList.add("d-none")
    this.writeTabTarget.classList.add("active")
    this.previewTabTarget.classList.remove("active")
    this.writeTabTarget.setAttribute("aria-selected", "true")
    this.previewTabTarget.setAttribute("aria-selected", "false")
  }

  preview(event) {
    event.preventDefault()
    const text = this.editorTarget.value
    this.previewTarget.innerHTML = this.renderMarkdown(text)
    this.editorTarget.classList.add("d-none")
    this.previewTarget.classList.remove("d-none")
    this.writeTabTarget.classList.remove("active")
    this.previewTabTarget.classList.add("active")
    this.writeTabTarget.setAttribute("aria-selected", "false")
    this.previewTabTarget.setAttribute("aria-selected", "true")
  }

  // --- Image upload -------------------------------------------------------
  //
  // Remote <img> is stripped server-side (see ForumHtmlHardener), so uploading
  // is the only way to get a picture into a post. Paste, drag-and-drop and the
  // toolbar button all funnel into uploadFiles.

  pickImage(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  uploadPicked() {
    this.uploadFiles(this.fileInputTarget.files)
    this.fileInputTarget.value = ""
  }

  paste(event) {
    const files = this.imageFilesFrom(event.clipboardData)
    if (files.length === 0) return

    // Only swallow the paste when it actually carries an image, so pasting
    // text alongside a screenshot still behaves normally.
    event.preventDefault()
    this.uploadFiles(files)
  }

  // Must gate on `types`, not `files`. During dragover the drag data store is
  // in protected mode, so dataTransfer.files is ALWAYS empty — checking it
  // meant preventDefault never ran, drop never fired, and the browser's default
  // handler navigated the tab to the dropped image, destroying the draft.
  dragover(event) {
    if (Array.from(event.dataTransfer?.types || []).includes("Files")) event.preventDefault()
  }

  drop(event) {
    // preventDefault FIRST, unconditionally. dragover accepts any drag whose
    // types include "Files", which makes the textarea a drop target for every
    // file type — so returning early here without preventing the default let
    // the browser navigate the tab to a dropped PDF or zip, losing the draft.
    // That is the same failure dragover exists to stop, just for non-images.
    event.preventDefault()

    const files = this.imageFilesFrom(event.dataTransfer)
    if (files.length === 0) {
      this.setStatus("Only images can be uploaded.", true)
      return
    }

    this.uploadFiles(files)
  }

  imageFilesFrom(source) {
    if (!source || !source.files) return []
    return Array.from(source.files).filter((file) => file.type.startsWith("image/"))
  }

  async uploadFiles(files) {
    for (const file of Array.from(files)) {
      await this.uploadFile(file)
    }
  }

  async uploadFile(file) {
    this.setStatus(`Uploading ${file.name}…`)

    const body = new FormData()
    body.append("file", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body: body,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
        credentials: "same-origin"
      })
      // Check the response BEFORE parsing. A session that expired mid-compose
      // redirects to the sign-in page, which fetch follows transparently — so
      // json() would throw on HTML and the catch below would report a generic
      // "try again" while the member retries forever without being told to
      // sign in.
      if (!response.redirected && response.ok && this.isJson(response)) {
        const payload = await response.json()
        this.insertAtCursor(`![${payload.alt || "image"}](${payload.url})\n`)
        this.setStatus("")
        return
      }

      if (response.redirected || response.status === 401 || response.status === 403) {
        this.setStatus("Your session expired — please sign in again to upload.", true)
        return
      }

      const message = this.isJson(response) ? (await response.json()).error : null
      this.setStatus(message || "Upload failed.", true)
    } catch {
      this.setStatus("Upload failed. Please try again.", true)
    }
  }

  isJson(response) {
    return (response.headers.get("content-type") || "").includes("application/json")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  setStatus(message, isError = false) {
    if (!this.hasUploadStatusTarget) return
    this.uploadStatusTarget.textContent = message
    this.uploadStatusTarget.classList.toggle("text-danger", isError)
  }

  // --- Chart embed --------------------------------------------------------

  insertChart(event) {
    event.preventDefault()
    // Insert the wrapper and park the cursor where the ticker goes, rather than
    // opening a prompt() — a modal dialog for a four-character answer.
    const snippet = "[chart:]"
    this.insertAtCursor(snippet)

    const editor = this.editorTarget
    const caret = editor.selectionStart - 1
    editor.selectionStart = editor.selectionEnd = caret
    editor.focus()
  }

  // --- Shared insertion ---------------------------------------------------

  insertAtCursor(text) {
    const editor = this.editorTarget
    const start = editor.selectionStart
    const end = editor.selectionEnd

    editor.value = editor.value.slice(0, start) + text + editor.value.slice(end)
    editor.selectionStart = editor.selectionEnd = start + text.length
    editor.focus()
  }

  handleEditorInput() {
    clearTimeout(this.debounceTimer)

    const mentionQuery = this.getMentionQuery()
    if (mentionQuery) {
      this.debounceTimer = setTimeout(() => {
        this.searchMentions(mentionQuery)
      }, 200)
    } else {
      this.closeMentionDropdown()
    }
  }

  getMentionQuery() {
    const editor = this.editorTarget
    const text = editor.value
    const cursorPos = editor.selectionStart

    // Look backward from cursor for @pattern
    const beforeCursor = text.substring(0, cursorPos)
    const match = beforeCursor.match(/@([a-zA-Z0-9_]{1,30})$/)
    return match ? match[1] : null
  }

  async searchMentions(query) {
    try {
      const response = await fetch(`/forum/mentions?q=${encodeURIComponent(query)}`)
      if (!response.ok) return

      this.mentionResults = await response.json()
      if (this.mentionResults.length > 0) {
        this.renderMentionDropdown()
      } else {
        this.closeMentionDropdown()
      }
    } catch {
      this.closeMentionDropdown()
    }
  }

  renderMentionDropdown() {
    if (!this.mentionDropdown) {
      this.mentionDropdown = document.createElement("div")
      this.mentionDropdown.className = "mention-dropdown"
      this.mentionDropdown.id = "forum-mention-listbox"
      this.mentionDropdown.setAttribute("role", "listbox")
      this.editorTarget.parentNode.style.position = "relative"
      this.editorTarget.parentNode.appendChild(this.mentionDropdown)
    }

    this.editorTarget.setAttribute("role", "combobox")
    this.editorTarget.setAttribute("aria-controls", "forum-mention-listbox")
    this.editorTarget.setAttribute("aria-expanded", "true")

    this.mentionIndex = 0
    this.mentionDropdown.innerHTML = this.mentionResults
      .map((result, i) =>
        `<div class="mention-item${i === 0 ? ' active' : ''}" id="forum-mention-option-${i}" role="option" aria-selected="${i === 0}" data-alias="${result.alias}">@${result.alias}</div>`
      )
      .join("")

    this.editorTarget.setAttribute("aria-activedescendant", "forum-mention-option-0")

    this.mentionDropdown.querySelectorAll(".mention-item").forEach((item) => {
      item.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.selectMention(item.dataset.alias)
      })
    })
  }

  handleMentionKeydown(event) {
    if (!this.mentionDropdown || this.mentionResults.length === 0) return

    const items = this.mentionDropdown.querySelectorAll(".mention-item")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.mentionIndex = Math.min(this.mentionIndex + 1, items.length - 1)
        this.updateActiveItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.mentionIndex = Math.max(this.mentionIndex - 1, 0)
        this.updateActiveItem(items)
        break
      case "Enter":
      case "Tab":
        if (this.mentionIndex >= 0 && this.mentionIndex < items.length) {
          event.preventDefault()
          this.selectMention(items[this.mentionIndex].dataset.alias)
        }
        break
      case "Escape":
        event.preventDefault()
        this.closeMentionDropdown()
        break
    }
  }

  updateActiveItem(items) {
    items.forEach((item, i) => {
      const isActive = i === this.mentionIndex
      item.classList.toggle("active", isActive)
      item.setAttribute("aria-selected", isActive ? "true" : "false")
    })
    if (this.mentionIndex >= 0 && this.mentionIndex < items.length) {
      this.editorTarget.setAttribute("aria-activedescendant", items[this.mentionIndex].id)
    }
  }

  selectMention(alias_name) {
    const editor = this.editorTarget
    const text = editor.value
    const cursorPos = editor.selectionStart
    const beforeCursor = text.substring(0, cursorPos)
    const afterCursor = text.substring(cursorPos)

    // Replace the @partial with @full_alias
    const newBefore = beforeCursor.replace(/@[a-zA-Z0-9_]*$/, `@${alias_name} `)
    editor.value = newBefore + afterCursor
    editor.selectionStart = editor.selectionEnd = newBefore.length

    this.closeMentionDropdown()
    editor.focus()
  }

  closeMentionDropdown() {
    if (this.mentionDropdown) {
      this.mentionDropdown.remove()
      this.mentionDropdown = null
    }
    if (this.hasEditorTarget) {
      this.editorTarget.setAttribute("aria-expanded", "false")
      this.editorTarget.removeAttribute("aria-activedescendant")
    }
    this.mentionResults = []
    this.mentionIndex = -1
  }

  escapeAttribute(value) {
    return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  renderMarkdown(text) {
    if (!text.trim()) return '<p class="text-muted">Nothing to preview</p>'

    let html = text
    // Code blocks (fenced)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Strikethrough
    html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')
    // Images — must run before links, or `![alt](src)` matches the link
    // pattern and leaves a stray "!" behind.
    //
    // Mirrors ForumHtmlHardener: only Active Storage paths render, because a
    // remote src is stripped server-side. Showing it here would let an author
    // preview a picture the published post will not contain. src and alt are
    // escaped — this string goes into previewTarget.innerHTML, so an alt of
    // `x" onerror="…` would otherwise inject attributes into the author's page.
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
      const safeAlt = this.escapeAttribute(alt)
      if (!src.trim().startsWith("/rails/active_storage/")) {
        return `<span class="text-danger small">[image removed: only uploaded images are allowed]</span>`
      }
      return `<img src="${this.escapeAttribute(src)}" alt="${safeAlt}" class="img-fluid rounded">`
    })
    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="ugc nofollow noopener noreferrer">$1</a>')
    // Chart embeds — a placeholder here, since the real chart needs a server
    // round-trip the preview deliberately doesn't make.
    // Pattern must match ForumTextEnricher::CHART exactly (an ASX code leads
    // with a letter; a coin's -USD base just needs one), or the preview renders a chart for a token the server will
    // leave as literal text.
    html = html.replace(/\[chart:((?=\d*[A-Za-z])[A-Za-z0-9]{1,10}-[Uu][Ss][Dd]|[A-Za-z][A-Za-z0-9.]{0,9})\]/g, '<div class="border rounded p-3 text-muted small">Chart: $1</div>')
    // Ticker symbols ($BHP, $BTC-USD) - styled but non-clickable in preview.
    // Must match ForumTextEnricher::TICKER exactly.
    html = html.replace(/\$((?=\d*[A-Z])[A-Z0-9]{1,10}-USD|[A-Z][A-Z0-9]{0,9})\b/g, '<span class="ticker-link">$$$1</span>')
    // @mentions - styled but non-clickable in preview
    html = html.replace(/@([a-zA-Z0-9_]{3,30})\b/g, '<span class="mention-link">@$1</span>')
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>')
    html = html.replace(/\n/g, '<br>')
    html = `<p>${html}</p>`

    return html
  }
}
